import { API_URL } from '../../app/apiConfig';

// Persistent client-side memory cache for ultra-fast product rendering (TTL: 15 minutes)
const productDataCache = new Map(); // id -> { data, timestamp }
const inFlightRequests = new Map(); // id -> Promise
const CACHE_TTL_MS = 15 * 60 * 1000;

export function seedProductCache(products) {
  if (Array.isArray(products)) {
    const now = Date.now();
    products.forEach((p) => {
      if (p && p.id) {
        productDataCache.set(p.id, { data: p, timestamp: now });
      }
    });
  }
}

export function fetchProductById(id) {
  const now = Date.now();

  // Instant response if already cached in memory (<0.1ms)
  if (productDataCache.has(id)) {
    const cached = productDataCache.get(id);
    if (now - cached.timestamp < CACHE_TTL_MS) {
      return Promise.resolve({ data: cached.data });
    }
  }

  // Return cached promise if request is already in flight to avoid duplicate network calls
  if (inFlightRequests.has(id)) {
    return inFlightRequests.get(id);
  }

  const promise = (async () => {
    try {
      const response = await fetch(`${API_URL}/api/products/` + id);
      const data = await response.json();
      if (data && !data.error && (data.id || data._id)) {
        productDataCache.set(id, { data, timestamp: Date.now() });
      }
      return { data };
    } catch (error) {
      console.error('Error fetching product:', error);
      return { data: null, error };
    } finally {
      inFlightRequests.delete(id);
    }
  })();

  // Store the promise to prevent duplicate concurrent requests
  inFlightRequests.set(id, promise);
  return promise;
}

export function createProduct(product) {
  return new Promise(async (resolve) => {
    const response = await fetch(`${API_URL}/api/products/`, {
      method: 'POST',
      body: JSON.stringify(product),
      headers: { 'content-type': 'application/json' },
    });
    const data = await response.json();
    if (data && data.id) {
      productDataCache.set(data.id, { data, timestamp: Date.now() });
    }
    resolve({ data });
  });
}

export function updateProduct(update) {
  return new Promise(async (resolve) => {
    const response = await fetch(
      `${API_URL}/api/products/` + update.id,
      {
        method: 'PATCH',
        body: JSON.stringify(update),
        headers: { 'content-type': 'application/json' },
      }
    );
    const data = await response.json();
    if (data && data.id) {
      productDataCache.set(data.id, { data, timestamp: Date.now() });
    }
    resolve({ data });
  });
}

export function fetchProductsByFilters(filter, sort, pagination, admin) {
  let queryString = '';
  for (let key in filter) {
    const categoryValues = filter[key];
    if (categoryValues.length) {
      queryString += `${key}=${categoryValues}&`;
    }
  }
  for (let key in sort) {
    queryString += `${key}=${sort[key]}&`;
  }
  for (let key in pagination) {
    queryString += `${key}=${pagination[key]}&`;
  }
  if(admin){
    queryString += `admin=true`;
  }

  return new Promise(async (resolve) => {
    try {
      const response = await fetch(
        `${API_URL}/api/products?` + queryString
      );
      if (!response.ok) {
        resolve({ data: { products: [], totalItems: 0 } });
        return;
      }
      const data = await response.json();
      const totalItems = await response.headers.get('X-Total-Count');
      if (Array.isArray(data)) {
        seedProductCache(data);
        resolve({ data: { products: data, totalItems: +totalItems || data.length } });
      } else {
        resolve({ data: { products: [], totalItems: 0 } });
      }
    } catch (err) {
      resolve({ data: { products: [], totalItems: 0 } });
    }
  });
}

let categoriesMemoryCache = null;
export function fetchCategories() {
  if (categoriesMemoryCache) {
    return Promise.resolve({ data: categoriesMemoryCache });
  }
  return new Promise(async (resolve) => {
    try {
      const response = await fetch(`${API_URL}/api/categories`);
      const data = await response.json();
      if (Array.isArray(data)) {
        categoriesMemoryCache = data;
      }
      resolve({ data });
    } catch (err) {
      resolve({ data: [] });
    }
  });
}

let brandsMemoryCache = null;
export function fetchBrands() {
  if (brandsMemoryCache) {
    return Promise.resolve({ data: brandsMemoryCache });
  }
  return new Promise(async (resolve) => {
    try {
      const response = await fetch(`${API_URL}/api/brands`);
      const data = await response.json();
      if (Array.isArray(data)) {
        brandsMemoryCache = data;
      }
      resolve({ data });
    } catch (err) {
      resolve({ data: [] });
    }
  });
}

export function uploadProductThumbnail(file) {
  return new Promise(async (resolve, reject) => {
    try {
      const formData = new FormData();
      formData.append('image', file);

      const token = localStorage.getItem('token');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URL}/api/products/upload-thumbnail`, {
        method: 'POST',
        headers,
        body: formData,
        credentials: 'include',
      });

      const data = await response.json();
      if (response.ok && data.success) {
        resolve({ data });
      } else {
        reject(data.error || 'Failed to upload image to Hostinger');
      }
    } catch (err) {
      reject(err.message || 'Network error while uploading image');
    }
  });
}
